<template>
  <div :class="classes()">
    <IntroBanner
      :headline="$t('cs.wizard.introBanner.headline')"
      :copy="$t('cs.wizard.introBanner.copy')"
      :bottomSpace="40"
      :localStorage="LS_PFLEGELEISTUNGSFINDER_INFO_BANNER"
    />

    <IntroBanner
      v-if="healthDataLoaded && !careLevel && !calculatedCareLevel"
      :headline="$t('cs.wizard.introBanner.error.headline')"
      :copy="$t('cs.wizard.introBanner.error.copy')"
      :bottomSpace="40"
      highlightColor="supportWarning"
      notCloseable
      iconName="error-warning-line"
    >
      <div class="u-margin__top--2">
        <Link
          :to="ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD"
          icon="arrow-right-line"
          iconPos="left"
          noUnderline
        >
          {{ $t('cs.wizard.introBanner.error.link1') }}
        </Link>
      </div>
      <div class="u-margin__top--1">
        <Link @click="openModal" icon="arrow-right-line" iconPos="left" noUnderline>
          {{ $t('cs.wizard.introBanner.error.link2') }}
        </Link>
      </div>
    </IntroBanner>

    <div :class="`${el}__inner`">
      <Statusbar
        :headline="$t('cs.wizard.statusbar.headline')"
        :statusText="$t('cs.wizard.statusbar.done')"
        :currentStep="step + 1"
        :steps="stepLength"
        :class="`${el}__statusBar`"
      />
      <Divider v-if="$matchMedia.l" dotted />

      <div :class="`${el}__row`">
        <InViewAnimation v-if="headline">
          <Headline slot="1" :staticClass="`${el}__moduleHeadline`" size="m" responsive centered>
            {{ headline }}
          </Headline>
        </InViewAnimation>

        <div :class="`${el}__content`">
          <component
            :is="content.component.name"
            :id="content.id"
            :content="content.component.props"
            :el="el"
          />
        </div>
      </div>

      <Divider v-if="$matchMedia.l" dotted />

      <div :class="`${el}__row`">
        <div :class="`${el}__buttonRow`">
          <Link
            icon="arrow-left-line"
            :class="{
              [`${el}__navLink`]: true,
              [`${el}__navLink--hidden`]: step === 0 || mode === 'edit',
            }"
            @click="prev"
            noUnderline
          >
            {{ $t('cs.wizard.button.prev') }}
          </Link>

          <Button
            :label="
              step === stepLength - 1
                ? $t('cs.wizard.button.toResult')
                : $t(mode === 'edit' ? 'cs.wizard.button.endAfterEdit' : 'cs.wizard.button.next')
            "
            additionalIconName="arrow-right-line"
            :loading="loading"
            @click="next"
            small
            :disabled="!careServiceStepIsValid || (!careLevel && !calculatedCareLevel)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Container from '@/objects/Container';
import Headline from '@/objects/Headline';
import Link from '@/objects/Link';
import Button from '@/objects/Button';
import Divider from '@/objects/Divider';
import InViewAnimation from '@/components/InViewAnimation';
import Statusbar from '@/components/Statusbar';
import IntroBanner from '@/components/IntroBanner';

import Overview from './subcomponents/Overview';
import SwitcherRow from './subcomponents/SwitcherRow';
import ServiceToggles from './subcomponents/ServiceToggles';

import {
  mapFieldsCareService,
  mapFieldsHealth,
  mapFieldsCareLevelCalculator,
  mapFieldsUser,
} from '@/store/mapFields';

import mixin from '@/mixins/careService';

import careServiceWizard from '@/content/services/CareService/CareService';
import { LS_PFLEGELEISTUNGSFINDER_INFO_BANNER } from '@/constants/storage';
import { HEALTH_TOGGLE_CARE_LEVEL_MODAL } from '@/store/actions/health';
import { TASKS_UPDATE_CURRENT_TASK } from '@/store/actions/tasks';

import {
  CARESERVICE_SAVE,
  CARESERVICE_CLEAR_WIZARD,
  CARESERVICE_UPDATE_CARELEVEL,
} from '@/store/actions/careService';

import {
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
  ROUTE_SERVICE_PFLEGELEISTUNGSFINDER,
  ROUTE_ANALYSE_CARE_SERVICE,
} from '@/constants/routes';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';

export default {
  name: 'PflegeleistungsfinderWizard',
  props: {},
  components: {
    Headline,
    Link,
    Button,
    Divider,
    Container,
    InViewAnimation,
    Statusbar,
    IntroBanner,
    Overview,
    SwitcherRow,
    ServiceToggles,
  },
  mixins: [mixin],
  computed: {
    ...mapFieldsCareService(['careServiceStepIsValid']),
    ...mapFieldsHealth(['dependentPerson']),
    ...mapFieldsCareLevelCalculator(['calculatedCareLevel']),
    ...mapFieldsUser(['user']),

    step() {
      let { step } = this.$route.params;
      return parseInt(step) || 0;
    },

    stepLength() {
      if (!this.careLevel) {
        return careServiceWizard.filter((csw) => csw.id !== 'serviceToggles-0').length;
      } else {
        return careServiceWizard.length;
      }
    },

    headline() {
      return this.content.headline ? this.getItem(this.content.headline) : '';
    },

    content() {
      this.trackClick('', 'wizard_step_show');
      return careServiceWizard[this.step];
    },
  },
  data() {
    return {
      loading: false,
      LS_PFLEGELEISTUNGSFINDER_INFO_BANNER,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
      mode: '',
      el: 'v-plfWizard',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  created() {
    if (this.step === 0) {
      this.$store.dispatch(CARESERVICE_CLEAR_WIZARD);
    }

    const { mode } = this.$route.query;
    if (mode) {
      this.mode = mode;
    }
    if (this.step > 0 && !this.careServiceWizardIsFinished) {
      this.$router.push(ROUTE_SERVICE_PFLEGELEISTUNGSFINDER);
    }
  },
  methods: {
    prev() {
      let prevStep = this.step - 1;

      let route = `${ROUTE_SERVICE_PFLEGELEISTUNGSFINDER}/${prevStep}`;

      if (this.step === 0) {
        route = `${ROUTE_SERVICE_PFLEGELEISTUNGSFINDER}/3`;
      }

      this.trackClick('_link-' + this.$t('cs.wizard.button.prev'), 'link');
      this.$router.push(route);
    },

    next() {
      if (this.mode === 'edit') {
        this.end();
        return;
      }

      let route = `${ROUTE_SERVICE_PFLEGELEISTUNGSFINDER}/${this.step + 1}`;

      if (this.step === this.stepLength - 1) {
        this.trackClick('_btn-' + this.$t('cs.wizard.button.toResult'), 'button');
        this.end();
        return;
      }

      const label =
        '_btn-' +
        this.$t(this.mode === 'edit' ? 'cs.wizard.button.endAfterEdit' : 'cs.wizard.button.next');
      this.trackClick(label, 'button');

      this.$router.push(route);
    },

    redirect() {
      this.$router.push(ROUTE_ANALYSE_CARE_SERVICE).then(() => {
        this.loading = false;
      });
    },

    end() {
      this.loading = true;

      if (!this.careLevel && this.calculatedCareLevel) {
        this.$store.dispatch(CARESERVICE_UPDATE_CARELEVEL, {
          careServiceCareLevel: this.calculatedCareLevel,
        });
      }

      const userInputValue = this.formatNumber(
        this.calculatedBasics.pflegegeld.percent,
        0,
        true
      ).toString();

      this.$store
        .dispatch(CARESERVICE_SAVE, { careServiceWizardIsFinished: true, userInputValue })
        .then(() => {
          if (this.careLevel) {
            this.$store
              .dispatch(TASKS_UPDATE_CURRENT_TASK, 'P3-30')
              .then(this.redirect)
              .catch(this.redirect);
          } else {
            this.redirect();
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    getItem(item) {
      if (!item) return '';

      if (item.length === 1) {
        return item[0];
      }

      return item[this.dependentPerson === 'other' ? 1 : 0];
    },

    openModal() {
      this.$store.dispatch(HEALTH_TOGGLE_CARE_LEVEL_MODAL);
    },

    trackClick(label, element) {
      const product = 'Pflegeleistungsfinder';
      let stepLabel = this.step + 1;
      let eventAction = '';

      if (element !== 'wizard_step_show') {
        eventAction = '-click';
      }

      switch (this.step) {
        case 0:
          stepLabel = stepLabel + '-Pflege und Betreuung';
          break;
        case 1:
          stepLabel = stepLabel + '-Leichte Unterstützung';
          break;
        case 2:
          stepLabel = stepLabel + '-Alltagsgestaltung';
          break;
        case 3:
          stepLabel = stepLabel + '-Hauswirtschaftliche Versorgung';
          break;
        case 4:
          stepLabel = stepLabel + '-medizinische Versorgung';
          break;
        case 5:
          stepLabel = stepLabel + '-bereits genehmigt?';
          break;
      }
      const labelText = product + '-wizard-step-' + stepLabel + label;

      createTrackingData(product, labelText, element, eventAction);
    },
  },
};
</script>
